import React, { useEffect, useRef, useState } from 'react';
import { getRecentCustomers, searchCustomers } from '../services/customers';
import './SearchBar.css';

/**
 * Customer search in the top bar — name, phone, email, address or postal code, across every customer
 * in Zoho. Tap a result → their account. Hidden while an inspection / report is being done (App.js).
 */
const prettyPhone = (p) => {
  const d = String(p || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : p || '';
};

const SearchBar = ({ onOpenCustomer }) => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [state, setState] = useState('idle'); // idle | searching | done | error
  const [error, setError] = useState('');
  const [active, setActive] = useState(-1);
  const wrap = useRef(null);
  const input = useRef(null);

  // wait for a pause in typing, and drop answers to older searches
  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) {
      setResults([]);
      setState('idle');
      return undefined;
    }
    const ctl = new AbortController();
    const timer = setTimeout(() => {
      setState('searching');
      searchCustomers(query, ctl.signal)
        .then((list) => {
          setResults(list);
          setActive(-1);
          setState('done');
        })
        .catch((err) => {
          if (err.name === 'AbortError') return;
          setError(err.message);
          setState('error');
        });
    }, 300);
    return () => {
      clearTimeout(timer);
      ctl.abort();
    };
  }, [q]);

  useEffect(() => {
    const away = (e) => wrap.current && !wrap.current.contains(e.target) && setOpen(false);
    document.addEventListener('mousedown', away);
    document.addEventListener('touchstart', away);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('touchstart', away);
    };
  }, []);

  const pick = (c) => {
    setOpen(false);
    setQ('');
    input.current && input.current.blur();
    onOpenCustomer(c);
  };

  const onKey = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
      input.current && input.current.blur();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, list.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter' && list.length) {
      e.preventDefault();
      pick(list[Math.max(active, 0)]);
    }
  };

  // nothing typed yet → the customers opened most recently on this phone
  const recent = open && q.trim().length < 2 ? getRecentCustomers() : [];
  const list = recent.length ? recent : results;
  const showPanel = open && (q.trim().length >= 2 || recent.length > 0);

  return (
    <div className="search" ref={wrap}>
      <span className="search-icon" aria-hidden="true">🔍</span>
      <input
        ref={input}
        className="search-input"
        type="search"
        placeholder="Search customers"
        aria-label="Search customers by name, phone, email, address or postal code"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        autoComplete="off"
        spellCheck="false"
      />
      {showPanel && (
        <div className="search-panel" role="listbox">
          {recent.length > 0 && <div className="search-heading">Recent</div>}
          {!recent.length && state === 'searching' && !results.length && <div className="search-note">Searching…</div>}
          {!recent.length && state === 'error' && <div className="search-note error">{error}</div>}
          {!recent.length && state === 'done' && !results.length && <div className="search-note">No customer matches “{q.trim()}”</div>}
          {list.map((c, i) => (
            <button
              key={c.id}
              type="button"
              role="option"
              aria-selected={i === active}
              className={`search-row${i === active ? ' active' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(c)}
            >
              <span className="search-row-top">
                <b>{c.name || 'No name'}</b>
                {c.status && <span className="search-status">{c.status}</span>}
              </span>
              <span className="search-row-sub">
                {[prettyPhone(c.phone), c.email].filter(Boolean).join(' · ')}
              </span>
              {(c.address || c.postalCode) && (
                <span className="search-row-sub">{[c.address, c.postalCode].filter(Boolean).join(' · ')}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
